/**
 * Insert `text` into an input/textarea at the current caret position
 * (replacing any selection), then place the caret right after the inserted
 * text.
 *
 * The value is set via the native value setter and an `input` event is
 * dispatched so React (and react-hook-form's onChange) picks up the change as
 * if the user had typed it — this keeps the existing onChange sanitizers and
 * form state in sync.
 */
export function insertAtCursor(
  el: HTMLInputElement | HTMLTextAreaElement | null,
  text: string
) {
  if (!el) return;

  // Read the selection before focusing — it is retained after blur.
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? el.value.length;
  const nextValue = el.value.slice(0, start) + text + el.value.slice(end);

  const prototype =
    el instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
  const nativeSetter = Object.getOwnPropertyDescriptor(
    prototype,
    "value"
  )?.set;

  if (nativeSetter) {
    nativeSetter.call(el, nextValue);
  } else {
    el.value = nextValue;
  }

  el.dispatchEvent(new Event("input", { bubbles: true }));

  const caret = start + text.length;
  el.focus();
  el.setSelectionRange(caret, caret);
}
